import { Organization, partialUpdateOrganization, retrieveOrganization, listSsoConfig } from '@goldcast/api/backend';
import logger from '@goldcast/logger';

const getDefaultState = () =>
  ({
    currentOrg: null,
    loadingOrgData: false,
    isFeatureFlagLoading: true,
    showEmailVerificationDialog: false,
    loadingAllowedEmailDomains: false
  }) as OrgStore;

export type CurrentOrganization = Organization & {
  useJotFormIntegration?: boolean;
  useLogrocketTrack?: boolean;
  useWebinarEvents?: boolean;
  notUseRegistrantsCount?: boolean;
  useWebinarAIIntegration?: boolean;
  assignQnaToSpeaker?: boolean;
  showVideoLibraryTranscriptDownload?: boolean;
  isOneClickRegisterEnabled?: boolean;
  useHLSGuard?: boolean;
  enableFormLevelFieldMapping?: boolean;
  clRecordingSupportStartDate?: number;
  useEventLocaleForDateTimeFormatting?: boolean;
  isCustomRolesEnabled?: boolean;
  enableDuplicateEventViaWorkflow?: boolean;
  use1080VideoQuality?: boolean;
  enableGlobalSSO?: boolean;
  enableAIInsightReport?: boolean;
  useCustomRolesManageUsersUIv2?: boolean;
  useNewChatApp?: boolean;
  useSalesforceCampaignCreationCustomSolution?: boolean;
  useNewNameCard: boolean;
  useRSAlignedFeeds?: boolean;
  enablePushSpeakerToStage?: boolean;
  useRSAlignedFeedsDate?: string;
  enableStudioMagiclinkInvalidation?: boolean;
  enable_magic_link_ttl?: boolean;
  useAIPageRevamp?: boolean;
  enable_ignore_s3_upload?: boolean;

  useNewSlideAnimations?: boolean;
  useFloatingEmoji?: boolean;
  useCLPlatformBrandkit?: boolean;
  enableEventInternalName?: boolean;
  enableCancelCalendarInviteOnDelete?: boolean;
  hideRegistrantCountWhenZero?: boolean;
  enableCertificateBuilder?: boolean;
  enableCertificateProfessionalEducation?: boolean;
  salesforceCampaignCloneInTemplate?: boolean;
  useCatchMeUp?: boolean;
  maxBulkUploadRecords?: number;
  useOnDemandMQL?: boolean;
  gdprTrackingConsentEnabled?: boolean;
  useOmniEmbed?: boolean;
  enableSeriesOnDemandBulkRegistration?: boolean;
  useVanityHttpValidation?: boolean;
  useRedirectUserToFailOverURL?: boolean;
  rtmpLowBandwidthMode?: boolean;
};

type OrgStore = {
  currentOrg: CurrentOrganization | null;
  loadingOrgData: boolean;
  isFeatureFlagLoading: boolean;
  showEmailVerificationDialog: boolean;
  loadingAllowedEmailDomains: boolean;
};

const state: OrgStore = getDefaultState();

const getters = {
  getCurrentOrgId: state => {
    return state.currentOrg ? state.currentOrg.id : null;
  },
  getRegistrationAppVanityDomain: (_, getters) => {
    const currentOrg = getters.getCurrentOrg;
    const domainSettings = currentOrg?.registration_app_vanity_domain_settings;
    return domainSettings
      ? domainSettings.filter?.(settings => settings.is_default && settings.is_verified)?.[0]?.domain
      : null;
  },
  getCurrentOrg: state => {
    return state.currentOrg;
  },
  getIsWorkspaceEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.is_workspace_enabled;
  },
  canCreateInhouseSimuliveBroadcasts: (_, getters) => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.enable_inhouse_simulive;
  },
  getOrgConfig: (_, getters) => configKeys => {
    const orgConfigs = {};
    const currentOrg = getters.getCurrentOrg;
    if (!currentOrg) {
      return orgConfigs;
    }

    configKeys.forEach(key => {
      orgConfigs[key] = currentOrg[key];
    });

    return orgConfigs;
  },
  useJotFormIntegration: (_, getters) => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.useJotFormIntegration;
  },
  isContentLabEnabled: (_, getters) => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.content_lab_enabled;
  },
  marketoConnectedBaseProgramID: (_, __, rootState) => {
    const currentIntegrationInstance = rootState.orgIntegration?.currentSolutionInstance;
    const integrationSettings = rootState.orgIntegration?.integrationSettings;
    const integrationSetting = integrationSettings?.find(
      setting => setting.entity_id === currentIntegrationInstance?.id
    );
    return integrationSetting?.base_marketo_program_id || '';
  },
  marketoCustomActivityErrorMessage(_, getters) {
    const currentOrg = getters.getCurrentOrg;
    if (!currentOrg) {
      return '';
    }

    const isGoldcastActivityCreated = Boolean(currentOrg.marketo_goldcast_event_custom_activity_id);
    if (isGoldcastActivityCreated) {
      return '';
    }

    const isUserProfileActivityCreated = Boolean(currentOrg.marketo_user_profile_id);
    const isUserEngagementActivityCreated = Boolean(currentOrg.marketo_user_engagement_id);
    let errorMessage = '';
    if (!isUserProfileActivityCreated) {
      errorMessage = `Failed to create a new Custom Activity: "User Profile"`;
    }
    if (!isUserEngagementActivityCreated) {
      errorMessage = `Failed to create a new Custom Activity: "User Engagement"`;
    }
    if (!isUserProfileActivityCreated && !isUserEngagementActivityCreated) {
      errorMessage = `Failed to create new Custom Activities: "User Profile" and "User Engagement"`;
    }

    return errorMessage;
  },
  isTrayAccountActivated(_, getters) {
    const currentOrg = getters.getCurrentOrg;
    return Boolean(currentOrg?.tray_id);
  },
  eloquaConnectedEventFormID: (_, getters) => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.eloqua_form_id || '';
  },
  isWebinarEventsEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.useWebinarEvents || false;
  },
  isEventRolesEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.is_event_roles_enabled || false;
  },
  isFeatureFlagsLoading: state => {
    return state.isFeatureFlagLoading;
  },
  isTeamLevelIntegrationEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.is_workspace_enabled;
  },
  isLoadingAllowedEmailDomains: state => {
    return state.loadingAllowedEmailDomains;
  },
  isCLPlatformBrandkitEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.useCLPlatformBrandkit || false;
  },
  isOnDemandMQLEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.useOnDemandMQL || false;
  },
  isOmniEmbedEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.useOmniEmbed || false;
  },
  isCertificateProfessionalEducationEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.enableCertificateProfessionalEducation || false;
  },
  isVanityHttpValidationEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.useVanityHttpValidation || false;
  },
  isRtmpLowBandwidthModeEnabled: (_, getters): boolean => {
    const currentOrg = getters.getCurrentOrg;
    return currentOrg?.rtmpLowBandwidthMode || false;
  }
};

const mutations = {
  setCurrentOrg(state, org) {
    state.currentOrg = {
      ...state.currentOrg,
      ...org
    };
  },
  setLoadingOrgData(state, loading) {
    state.loadingOrgData = loading;
  },
  clearOrgStore(state) {
    state.currentOrg = null;
    state.loadingOrgData = false;
  },
  setIsFeatureFlagLoading(state, isLoading) {
    state.isFeatureFlagLoading = isLoading;
  },
  setShowEmailVerificationDialog(state, show) {
    state.showEmailVerificationDialog = show;
  },
  setLoadingAllowedEmailDomains(state, loading) {
    state.loadingAllowedEmailDomains = loading;
  }
};

const actions = {
  async retrieveOrg(context, orgId: string) {
    context.commit('setLoadingOrgData', true);
    try {
      const org = await retrieveOrganization({ id: orgId });
      context.commit('setCurrentOrg', org);
      return org;
    } catch (err) {
      logger.error('[org] Failed to retrieve organization', { orgId, error: err });
      throw err;
    } finally {
      context.commit('setLoadingOrgData', false);
    }
  },
  async updateOrgData(context, { input }) {
    try {
      context.commit('setLoadingOrgData', true);
      if (!input.id) {
        input['id'] = context.state.currentOrg.id;
      }
      const org = await partialUpdateOrganization({
        id: input.id,
        body: input
      });
      context.commit('setCurrentOrg', org);
    } catch (err) {
      context.commit('setLoadingRecordings', false);
      context.commit('setErrorRecordings', err);
      throw err;
    } finally {
      context.commit('setLoadingOrgData', false);
    }
  },
  async loadOrgAllowedEmailDomains(context, orgId: string) {
    context.commit('setLoadingAllowedEmailDomains', true);
    try {
      const response = await listSsoConfig({ queryParams: { organization: orgId } });
      return (response[0]?.allowed_email_domains ?? []) as string[];
    } catch (err) {
      logger.error('[org] Failed to load allowed email domains', { orgId, error: err });
      return [];
    } finally {
      context.commit('setLoadingAllowedEmailDomains', false);
    }
  }
};

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
